import React, { FC, useCallback } from 'react';
import { Box, Checkbox, Flex, Text, useTheme } from '@chakra-ui/react';
import type { IPhoto } from 'ts/client/types';
import {
    PhotoActionList,
    PhotoIndicatorList,
    PhotoKebab,
    ViewPhotoOverlay
} from 'ts/common/components/gallery';
import { isSlideshowWithNoThumbnail } from 'ts/common/components/gallery/helpers';
import { useBreakpoint } from 'ts/common/hooks';
import type { IUsePhotoActionsProps } from 'ts/common/hooks/usePhotoActions';
import { getAssetMediaType } from 'ts/common/mediaType';
import { getMediaLabelKey } from 'ts/common/mediaTypeLabels';

interface Props extends IUsePhotoActionsProps {
    /** True if the user is in Compare mode */
    isComparing: boolean;
    /** True if the photo is in the cart */
    isInCart: boolean;
    /** True if the Package Builder is open */
    isPackageBuilderOpen?: boolean;
    /** True if the user selected enough photos for comparison */
    isReadyToCompare?: boolean;
    /** True if the photo is selected in Compare mode or the Package Builder */
    isSelected: boolean;
    /** Callback when the photo overlay is clicked */
    onClick: (photo: IPhoto, goTo?: boolean) => void;
}

const PhotoOverlay: FC<Props> = ({
    isBlackAndWhite,
    isComparing,
    isDownloadPossible,
    isFavorite,
    isHidden,
    isInCart,
    isLabeled,
    isPackageBuilderOpen = false,
    isReadyToCompare,
    isSelected,
    onClick,
    photo,
    photoActions,
    requiresDownloadDestination,
    spFreeDigitals,
    translateFilter,
    $rootScope
}) => {
    const isLargeScreen = useBreakpoint();
    const theme = useTheme();
    const mediaType = getAssetMediaType(photo);
    // Compare Photos is for still photos only. The package builder is not: a package can sell a
    // video or a slideshow, and whether this particular asset fits the tile being filled is
    // decided per product, not by hiding it from the grid.
    const isSelectingForCompare = isComparing && mediaType === 'photo';
    const isSelecting = isSelectingForCompare || isPackageBuilderOpen;
    // The filename is only sent when the gallery's Show Filenames setting is on. Touch devices
    // have no hover, so they keep showing it in the full-screen view only.
    const isFilenameVisible = isLargeScreen && !isSelecting && Boolean(photo.filename);
    // Screen readers get the filename from the thumbnail's alt text, so the visible label is hidden
    // from them to avoid announcing it twice. A slideshow placeholder has no image, so there the
    // label is the only place the name appears.
    const isFilenameAnnouncedByThumbnail = !isSlideshowWithNoThumbnail(photo);

    const handleClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            onClick(photo);
        },
        [onClick, photo]
    );

    const handleViewPhotoClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        onClick(photo, true);
    };

    return (
        <Flex
            flexDirection="column"
            justifyContent="space-between"
            height="100%"
            position="relative"
            role="group"
            borderStyle="solid"
            borderWidth="4px"
            borderColor={isSelected && isSelecting ? 'brand.primaryOverlay' : 'transparent'}
            onClick={handleClick}
        >
            <Flex justifyContent="space-between" zIndex={1}>
                {isSelecting && (
                    <Box padding="4px">
                        <Checkbox isChecked={isSelected} variant="round" size="xl" readOnly />
                    </Box>
                )}
                {isFilenameVisible && (
                    <Text
                        aria-hidden={isFilenameAnnouncedByThumbnail}
                        title={photo.filename}
                        flex="0 1 auto"
                        minWidth={0}
                        margin="4px"
                        paddingX="10px"
                        height="28px"
                        lineHeight="28px"
                        fontSize="12px"
                        color="text"
                        backgroundColor={
                            theme.isLightColorScheme ? 'whiteAlpha.700' : 'blackAlpha.700'
                        }
                        borderRadius="full"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        opacity={0}
                        transition="0.25s opacity"
                        sx={{
                            // Large touchscreens pass the breakpoint but would keep the label
                            // stuck on after a tap, so only reveal it where hover is real.
                            '@media (hover: hover)': {
                                '[role=group]:hover &, [role=group]:focus-within &': {
                                    opacity: 1
                                }
                            }
                        }}
                    >
                        {photo.filename}
                    </Text>
                )}
                {/* Keeps the indicators from shrinking, so a long filename truncates instead of
                    wrapping them onto a second row. */}
                <Box flex="1 0 auto">
                    <PhotoIndicatorList
                        isBlackAndWhite={isBlackAndWhite}
                        isFavorite={isFavorite}
                        isHidden={isHidden}
                        isInCart={isInCart}
                        isLabeled={isLabeled}
                    />
                </Box>
            </Flex>
            {isLargeScreen &&
                ((isSelectingForCompare && !isSelected && !isReadyToCompare) ||
                    isPackageBuilderOpen) && (
                    <Flex
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        position="absolute"
                        height="100%"
                        width="100%"
                        top={0}
                        left={0}
                        background="blackAlpha.500"
                        opacity={0}
                        _groupHover={{ opacity: 1 }}
                        transition="0.25s opacity"
                    >
                        <Text color="white">
                            {isSelectingForCompare && translateFilter('selectToCompare')}
                            {isPackageBuilderOpen &&
                                translateFilter(
                                    getMediaLabelKey(
                                        isSelected ? 'deselectPhoto' : 'selectPhoto',
                                        mediaType
                                    )
                                )}
                        </Text>
                    </Flex>
                )}
            {isSelectingForCompare && !isSelected && isReadyToCompare && (
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    position="absolute"
                    height="calc(100% + 8px)"
                    width="calc(100% + 8px)"
                    top="-4px"
                    left="-4px"
                    background="whiteAlpha.500"
                    opacity={1}
                    transition="0.25s opacity"
                />
            )}
            {isPackageBuilderOpen && (
                <ViewPhotoOverlay
                    mediaType={mediaType}
                    onClick={handleViewPhotoClick}
                    translate={translateFilter}
                />
            )}
            {!isSelecting && (
                <>
                    {isLargeScreen ? (
                        <PhotoActionList
                            isBlackAndWhite={isBlackAndWhite}
                            isDownloadPossible={isDownloadPossible}
                            isFavorite={isFavorite}
                            isHidden={isHidden}
                            isLabeled={isLabeled}
                            photo={photo}
                            photoActions={photoActions}
                            requiresDownloadDestination={requiresDownloadDestination}
                            spFreeDigitals={spFreeDigitals}
                            translateFilter={translateFilter}
                            $rootScope={$rootScope}
                        />
                    ) : (
                        <PhotoKebab
                            isBlackAndWhite={isBlackAndWhite}
                            isDownloadPossible={isDownloadPossible}
                            isFavorite={isFavorite}
                            isHidden={isHidden}
                            isLabeled={isLabeled}
                            photo={photo}
                            photoActions={photoActions}
                            requiresDownloadDestination={requiresDownloadDestination}
                            spFreeDigitals={spFreeDigitals}
                            translateFilter={translateFilter}
                            $rootScope={$rootScope}
                        />
                    )}
                </>
            )}
        </Flex>
    );
};

export default PhotoOverlay;
